import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addAccountAndRead, readStatement } from '../store';
import type { BankAccount } from '../types';

export interface ToastAction {
  label: string;
  onClick: () => void;
}
export type AddToast = (msg: string, action?: ToastAction, ms?: number) => void;

interface PendingAccount {
  account: BankAccount;
  fileName: string;
  lines: number;
  decide: (add: boolean) => void;
}

const READ_MS = 1100;
const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * "Upload bank statement" in the band (rcm-32 §2). The button opens the file
 * picker (CSV only, several files fine). While a file is read, a thin line shows
 * under the band and the button reads "Reading…". Each file ends in a toast; the
 * errors are in plain words, and a new account asks "Add it?" first.
 */
export function useStatementUpload(addToast: AddToast): { uploading: boolean; open: () => void; node: React.ReactNode } {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState<PendingAccount | null>(null);

  const readAll = async (files: File[]): Promise<void> => {
    setUploading(true);
    for (const file of files) {
      await wait(READ_MS);
      let text = '';
      try {
        text = await file.text();
      } catch {
        text = '';
      }
      const prefix = files.length > 1 ? `${file.name}: ` : '';
      const r = readStatement(file.name, text);
      if (r.status === 'error') {
        addToast(`${prefix}${r.message}`);
      } else if (r.status === 'new_account') {
        const add = await new Promise<boolean>((resolve) => {
          setPending({ account: r.account, fileName: file.name, lines: r.lines, decide: resolve });
        });
        setPending(null);
        if (add) {
          await wait(READ_MS);
          addToast(`${prefix}${addAccountAndRead(file.name, r.lines)}`, undefined, 12000);
        } else {
          addToast(`${prefix}Nothing was added from this file.`);
        }
      } else {
        addToast(
          `${prefix}${r.message}`,
          r.hasMismatch ? { label: "See what doesn't match", onClick: () => { void navigate('/posting/bank'); } } : undefined,
          12000,
        );
      }
    }
    setUploading(false);
  };

  const node = (
    <>
      <input
        ref={input}
        type="file"
        accept=".csv,text/csv"
        multiple
        className="po-vh"
        tabIndex={-1}
        aria-label="Upload the bank statement as a CSV file"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length > 0) void readAll(files);
        }}
      />
      {pending ? <NewAccountDialog p={pending} /> : null}
    </>
  );

  return { uploading, open: () => { input.current?.click(); }, node };
}

function NewAccountDialog({ p }: { p: PendingAccount }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') p.decide(false); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [p]);
  return (
    <div className="po-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) p.decide(false); }}>
      <div ref={ref} className="po-dialog" role="dialog" aria-modal="true" aria-label="New account">
        <h2 className="po-dialog-title">This is a new account: {p.account.short}. Add it?</h2>
        <p className="po-dialog-body">
          {p.fileName} has {p.lines.toLocaleString()} lines. ELVA keeps each account&apos;s deposits apart. Matched deposits can be filtered by account.
        </p>
        <div className="po-dialog-actions">
          <button type="button" className="po-btn po-btn-primary" onClick={() => { p.decide(true); }}>Add it</button>
          <button type="button" className="po-btn" onClick={() => { p.decide(false); }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
